import React, { useState } from 'react';
import CommonModal from '../../../Components/CommonModal';
import CommonLoader from '../../../Components/CommonLoader';
import { risk_portal_create_category } from '../../../services';

// Preset palette for a category's colour dot — CommonModal has no `color`
// field type, so we offer a named-colour select. Values are the hex the dot
// renders with; labels are human names for the dropdown.
export const CATEGORY_COLORS = [
    { value: '#6366f1', label: 'Indigo' },
    { value: '#3b82f6', label: 'Blue' },
    { value: '#0ea5e9', label: 'Sky' },
    { value: '#14b8a6', label: 'Teal' },
    { value: '#22c55e', label: 'Green' },
    { value: '#f59e0b', label: 'Amber' },
    { value: '#f97316', label: 'Orange' },
    { value: '#ef4444', label: 'Red' },
    { value: '#f43f5e', label: 'Rose' },
    { value: '#a855f7', label: 'Purple' },
    { value: '#64748b', label: 'Slate' },
];

// CategoryForm — Add a risk category. CommonModal owns the form (config-driven
// fields + validation). Create only — there's no update endpoint; delete lives
// on the category card in the page. Page owns the toast; this reports via
// callbacks. Payload: { name, description, color }.
//
// Props: show, onClose, onSuccess(msg), onError(msg).
export default function CategoryForm({ show, onClose, onSuccess, onError }) {
    const [busy, setBusy] = useState(false);

    const submit = async (data) => {
        setBusy(true);
        try {
            const payload = {
                name: (data.name || '').trim(),
                description: (data.description || '').trim(),
                color: data.color || CATEGORY_COLORS[0].value,
            };
            await risk_portal_create_category(payload);
            onSuccess?.(`Category "${payload.name}" created`);
            onClose?.();
        } catch (e) {
            onError?.(e?.response?.data?.error?.message || e?.response?.data?.response_data?.message || 'Failed to create category');
        } finally {
            setBusy(false);
        }
    };

    return (
        <CommonModal
            show={show}
            onHide={onClose}
            mid
            title="Add Category"
            subtitle="Categories organise your risk taxonomy (e.g. Cyber, Compliance, Operational)."
            fields={[
                { name: 'name', label: 'Name', type: 'text', half: true, required: 'Category name is required.' },
                { name: 'color', label: 'Colour', type: 'select', half: true, options: CATEGORY_COLORS },
                { name: 'description', label: 'Description', type: 'textarea', placeholder: 'What kinds of risk belong here?' },
            ]}
            defaultValues={{ name: '', color: CATEGORY_COLORS[0].value, description: '' }}
            onSubmit={submit}
            buttons={[
                { text: 'Cancel', variant: 'secondary', onClick: onClose },
                { text: 'Create', variant: 'primary', type: 'submit' },
            ]}
        >
            {busy && <CommonLoader overlay label="Saving…" />}
        </CommonModal>
    );
}
